import BaseApi from './base.api';

export interface CreateVisitRequest {
  noAl: string;
  visitDate: string; // Format: YYYY-MM-DD
  visitTime: string; // Format: HH:mm
  paymentType: number; // 1=NZOK, 2=Private, 3=Contract
  patientId: number;
  doctorId: number;
  disadvantaged?: boolean;
  incidentally?: boolean;
  anamnesis?: string;
  physicalExam?: string;
  examinations?: string;
  examType?: number; // 11=Primary, 12=Control, 13=Prophylactic
  
  // Diagnosis fields
  mainDiagnosisName?: string;
  mainDiagnosisMkbCode?: string;
  additionalDiagnoses?: string; // JSON string
  
  nonReimbursableTherapy?: string;
  signed?: boolean;
  
  // Visit type flags
  consultationVisit?: boolean;
  dispensaryVisit?: boolean;
  babyCareVisit?: boolean;
  hospitalReferral?: boolean;
  unfavorableVisit?: boolean;
  lkkVisit?: boolean;
  telkVisit?: boolean;
  
  // Prophylactic visit sub-types
  prophylacticChild?: boolean;
  prophylacticMother?: boolean;
  prophylacticOver18?: boolean;
  prophylacticRisk?: boolean;
  
  // Document flags
  telkForm?: boolean;
  notification?: boolean;
  epicrisis?: boolean;
  medicalNote?: boolean;
}

export interface UpdateVisitRequest {
  visitDate?: string;
  visitTime?: string;
  paymentType?: number;
  patientId?: number;
  doctorId?: number;
  disadvantaged?: boolean;
  incidentally?: boolean;
  anamnesis?: string;
  physicalExam?: string;
  examinations?: string;
  examType?: number;
  
  // Diagnosis fields
  mainDiagnosisName?: string;
  mainDiagnosisMkbCode?: string;
  additionalDiagnoses?: string; // JSON string
  
  nonReimbursableTherapy?: string;
  signed?: boolean;
  
  // Visit type flags
  consultationVisit?: boolean;
  dispensaryVisit?: boolean;
  babyCareVisit?: boolean;
  hospitalReferral?: boolean;
  unfavorableVisit?: boolean;
  lkkVisit?: boolean;
  telkVisit?: boolean;
  
  // Prophylactic visit sub-types
  prophylacticChild?: boolean;
  prophylacticMother?: boolean;
  prophylacticOver18?: boolean;
  prophylacticRisk?: boolean;
  
  // Document flags
  telkForm?: boolean;
  notification?: boolean;
  epicrisis?: boolean;
  medicalNote?: boolean;
}

export interface VisitResponse {
  id: number;
  noAl: string;
  visitDate: string;
  visitTime: string;
  paymentType: number;
  
  // Patient info
  patientId: number;
  patientName: string;
  patientEgn: string;
  
  // Doctor info
  doctorId: number;
  doctorName: string;
  doctorUinNumber: string;
  
  disadvantaged: boolean;
  incidentally: boolean;
  anamnesis?: string;
  physicalExam?: string;
  examinations?: string;
  examType: number;
  
  // Diagnosis fields
  mainDiagnosisName?: string;
  mainDiagnosisMkbCode?: string;
  additionalDiagnoses?: string; // JSON string
  
  nonReimbursableTherapy?: string;
  signed: boolean;
  
  // Visit type flags
  consultationVisit: boolean;
  dispensaryVisit: boolean;
  babyCareVisit: boolean;
  hospitalReferral: boolean;
  unfavorableVisit: boolean;
  lkkVisit: boolean;
  telkVisit: boolean;
  
  // Prophylactic visit sub-types
  prophylacticChild: boolean;
  prophylacticMother: boolean;
  prophylacticOver18: boolean;
  prophylacticRisk: boolean;
  
  // Document flags
  telkForm: boolean;
  notification: boolean;
  epicrisis: boolean;
  medicalNote: boolean;
  
  createdAt: string;
  updatedAt: string;
}

export interface VisitPageResponse {
  content: VisitResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
}

export interface DoctorStatistics {
  totalVisits: number;
  visitsThisMonth: number;
  visitsThisYear: number;
  totalPatients: number;
  newPatientsThisMonth: number;
  totalPrescriptions: number;
  visitTypeBreakdown: { [key: string]: number };
  visitsLastWeek: number;
  averageVisitsPerDay: number;
  topDiagnoses: { [key: string]: number };
}

class VisitApi extends BaseApi {
  // Create a new visit
  async createVisit(visitData: CreateVisitRequest): Promise<VisitResponse> {
    return this.post('/visits', visitData);
  }

  // Get all visits
  async getAllVisits(): Promise<VisitResponse[]> {
    return this.get('/visits');
  }

  // Get visits with pagination
  async getVisitsPaginated(page: number = 0, size: number = 20, sortBy: string = 'createdAt', sortDir: string = 'asc'): Promise<VisitPageResponse> {
    return this.get('/visits/paginated', {
      params: { page, size, sortBy, sortDir }
    });
  }

  // Get visit by ID
  async getVisitById(id: number): Promise<VisitResponse> {
    return this.get(`/visits/${id}`);
  }

  // Get visit by noAl (visit number)
  async getVisitByNoAl(noAl: string): Promise<VisitResponse> {
    return this.get(`/visits/noal/${noAl}`);
  }

  // Get visits by patient ID
  async getVisitsByPatientId(patientId: number): Promise<VisitResponse[]> {
    return this.get(`/visits/patient/${patientId}`);
  }

  // Get visits by doctor ID (DEPRECATED - use paginated version)
  async getVisitsByDoctorId(doctorId: number): Promise<VisitResponse[]> {
    console.warn('DEPRECATED: Use getVisitsByDoctorIdPaginated instead');
    return this.get(`/visits/doctor/${doctorId}`);
  }

  // Get visits by doctor ID with pagination
  async getVisitsByDoctorIdPaginated(
    doctorId: number, 
    page: number = 0, 
    size: number = 20,
    sortBy: string = 'visitDate',
    sortDir: string = 'desc'
  ): Promise<VisitPageResponse> {
    return this.get(`/visits/doctor/${doctorId}/paginated?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  }

  // Get recent visits by doctor ID (limited)
  async getRecentVisitsByDoctorId(doctorId: number, limit: number = 10): Promise<VisitResponse[]> {
    return this.get(`/visits/doctor/${doctorId}/recent?limit=${limit}`);
  }

  // Get doctor statistics
  async getDoctorStatistics(doctorId: number): Promise<DoctorStatistics> {
    return this.get(`/visits/doctor/${doctorId}/statistics`);
  }

  // Get visits by date range
  async getVisitsByDateRange(startDate: string, endDate: string): Promise<VisitResponse[]> {
    return this.get('/visits/date-range', {
      params: { startDate, endDate }
    });
  }

  // Update visit
  async updateVisit(id: number, visitData: UpdateVisitRequest): Promise<VisitResponse> {
    return this.put(`/visits/${id}`, visitData);
  }

  // Delete visit
  async deleteVisit(id: number): Promise<void> {
    return this.delete(`/visits/${id}`);
  }

  // Get visit count by patient ID
  async getVisitCountByPatientId(patientId: number): Promise<number> {
    return this.get(`/visits/patient/${patientId}/count`);
  }

  // Get visit count by doctor ID
  async getVisitCountByDoctorId(doctorId: number): Promise<number> {
    return this.get(`/visits/doctor/${doctorId}/count`);
  }
}

export const visitApi = new VisitApi();